import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Stage,
  Layer,
  Line,
  Circle,
  Rect,
  Arrow,
  Text as KonvaText,
  Group,
  Image as KonvaImage,
} from 'react-konva';
import { useDropzone } from 'react-dropzone';
import {
  Image as ImageIcon,
  Close,
  ChevronLeft,
  ChevronRight,
  Menu,
} from '@mui/icons-material';
import SidebarContent from './components/SidebarContent';
import { useHistory } from './hooks/useHistory';
import { loadImageFromFile, getImageFileFromDataTransfer } from './utils/image';
import './App.css';

const NAVBAR_HEIGHT = 56;
const SIDEBAR_WIDTH = 280;
const MOBILE_BREAKPOINT = 768;
const TOOL_PREFERENCES_KEY = 'rbg-tool-preferences';

const DEFAULT_PREFS = {
  lineColor: '#ffffff',
  lineWidth: 8,
  xColor: '#dc2626',
  xMarkSize: 30,
  circleColor: 'rgba(255, 0, 0, 0.7)',
  circleSize: 37,
  arrowColor: '#facc15',
  rectColor: '#22d3ee',
  textColor: '#ffffff',
  textSize: 24,
  numberColor: '#dc2626',
  numberSize: 22,
};

const DRAG_TOOLS = ['line', 'arrow', 'rect'];

const loadStoredPreferences = () => {
  if (typeof window === 'undefined') return { tool: 'line', prefs: DEFAULT_PREFS };
  try {
    const stored = window.localStorage.getItem(TOOL_PREFERENCES_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    return {
      tool: parsed.tool ?? 'line',
      // Merge over defaults so newly added tools/options have sane values even
      // for users with older saved preferences.
      prefs: { ...DEFAULT_PREFS, ...parsed.prefs },
    };
  } catch (error) {
    console.warn('Failed to load tool preferences from localStorage:', error);
    return { tool: 'line', prefs: DEFAULT_PREFS };
  }
};

// Strip the file extension for a friendlier download name.
const stripExtension = (name) => (name ? name.replace(/\.[^/.]+$/, '') : 'board');

// Next sequential number for a numbered marker, derived from existing markers
// so it stays consistent across undo/redo.
const nextMarkerNumber = (marks) => {
  const values = marks.filter((m) => m.type === 'number').map((m) => m.value);
  return (values.length ? Math.max(...values) : 0) + 1;
};

const App = () => {
  const initial = useRef(loadStoredPreferences());

  const [tool, setTool] = useState(initial.current.tool);
  const [prefs, setPrefs] = useState(initial.current.prefs);

  const history = useHistory([]);
  const marks = history.present;

  const [isDrawing, setIsDrawing] = useState(false);
  const [image, setImage] = useState(null);
  const [imageName, setImageName] = useState('board');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [toast, setToast] = useState(null);
  const [stageSize, setStageSize] = useState(() => {
    const sidebar = window.innerWidth < MOBILE_BREAKPOINT ? 0 : SIDEBAR_WIDTH;
    return {
      width: Math.max(window.innerWidth - sidebar, 0),
      height: Math.max(window.innerHeight - NAVBAR_HEIGHT, 0),
    };
  });
  const [scale, setScale] = useState(1);

  const stageRef = useRef(null);
  const draftRef = useRef(null);
  const gestureBeforeRef = useRef(null);
  const toastTimer = useRef(null);

  const setPref = useCallback((key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  }, []);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }, []);

  // Persist tool + preferences.
  useEffect(() => {
    try {
      window.localStorage.setItem(TOOL_PREFERENCES_KEY, JSON.stringify({ tool, prefs }));
    } catch (error) {
      console.warn('Failed to save tool preferences to localStorage:', error);
    }
  }, [tool, prefs]);

  // Track mobile breakpoint.
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recompute stage size + scale whenever the image, sidebar, or window changes.
  useEffect(() => {
    const recalculate = () => {
      const sidebarWidth = sidebarOpen && !isMobile ? SIDEBAR_WIDTH : 0;
      const maxWidth = window.innerWidth - sidebarWidth;
      const maxHeight = window.innerHeight - NAVBAR_HEIGHT;

      if (!image) {
        setStageSize({ width: maxWidth, height: maxHeight });
        setScale(1);
        return;
      }

      const imageAspect = image.width / image.height;
      const containerAspect = maxWidth / maxHeight;
      let width, height;
      if (imageAspect > containerAspect) {
        width = maxWidth;
        height = maxWidth / imageAspect;
      } else {
        width = maxHeight * imageAspect;
        height = maxHeight;
      }
      setStageSize({ width, height });
      setScale(width / image.width);
    };

    recalculate();
    window.addEventListener('resize', recalculate);
    return () => window.removeEventListener('resize', recalculate);
  }, [image, sidebarOpen, isMobile]);

  const applyNewImage = useCallback(
    (img, name) => {
      setImage(img);
      setImageName(stripExtension(name));
      history.reset([]);
    },
    [history]
  );

  const handleImageFile = useCallback(
    (file) => {
      if (!file) return;
      loadImageFromFile(file)
        .then((img) => applyNewImage(img, file.name))
        .catch(() => showToast('Could not load that image', 'error'));
    },
    [applyNewImage, showToast]
  );

  const handleImageUpload = (e) => {
    handleImageFile(e.target.files[0]);
    e.target.value = '';
  };

  // Paste an image from the clipboard (Ctrl/Cmd+V).
  useEffect(() => {
    const handlePaste = (e) => {
      const file = getImageFileFromDataTransfer(e.clipboardData);
      if (file) {
        e.preventDefault();
        handleImageFile(file);
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handleImageFile]);

  // Drag-and-drop an image file anywhere over the canvas.
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: (files) => handleImageFile(files[0]),
  });

  const getPointer = (e) => {
    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    return { x: pos.x / scale, y: pos.y / scale };
  };

  const handleMouseDown = (e) => {
    if (!image) return;
    if (e.evt && e.evt.cancelable) e.evt.preventDefault();

    const { x, y } = getPointer(e);

    if (DRAG_TOOLS.includes(tool)) {
      gestureBeforeRef.current = marks;
      draftRef.current = { x, y, lastX: x, lastY: y };
      let draft;
      if (tool === 'line') {
        draft = { type: 'line', points: [x, y], color: prefs.lineColor, strokeWidth: prefs.lineWidth };
      } else if (tool === 'arrow') {
        draft = { type: 'arrow', points: [x, y, x, y], color: prefs.arrowColor, strokeWidth: prefs.lineWidth };
      } else {
        draft = { type: 'rect', x, y, width: 0, height: 0, color: prefs.rectColor, strokeWidth: prefs.lineWidth };
      }
      history.update((prev) => [...prev, draft]);
      setIsDrawing(true);
      return;
    }

    // Single-click tools commit immediately as one history entry.
    if (tool === 'circle') {
      history.set((prev) => [
        ...prev,
        { type: 'circle', x, y, radius: prefs.circleSize, fill: prefs.circleColor },
      ]);
    } else if (tool === 'x') {
      history.set((prev) => [
        ...prev,
        { type: 'x', points: [x, y, prefs.xMarkSize], color: prefs.xColor, strokeWidth: prefs.lineWidth },
      ]);
    } else if (tool === 'number') {
      history.set((prev) => [
        ...prev,
        { type: 'number', x, y, value: nextMarkerNumber(prev), color: prefs.numberColor, radius: prefs.numberSize },
      ]);
    } else if (tool === 'text') {
      const text = window.prompt('Enter label text:');
      if (text && text.trim()) {
        history.set((prev) => [
          ...prev,
          { type: 'text', x, y, text: text.trim(), color: prefs.textColor, fontSize: prefs.textSize },
        ]);
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !image) return;
    if (e.evt && e.evt.cancelable) e.evt.preventDefault();

    const { x, y } = getPointer(e);
    if (draftRef.current) {
      draftRef.current.lastX = x;
      draftRef.current.lastY = y;
    }

    history.update((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      let updated;
      if (last.type === 'line') {
        updated = { ...last, points: [...last.points, x, y] };
      } else if (last.type === 'arrow') {
        updated = { ...last, points: [last.points[0], last.points[1], x, y] };
      } else if (last.type === 'rect') {
        updated = { ...last, width: x - last.x, height: y - last.y };
      } else {
        return prev;
      }
      return [...prev.slice(0, -1), updated];
    });
  };

  const handleMouseUp = () => {
    if (!image || !isDrawing) return;
    setIsDrawing(false);

    const draft = draftRef.current;
    draftRef.current = null;
    const moved =
      draft && (Math.abs(draft.lastX - draft.x) > 2 || Math.abs(draft.lastY - draft.y) > 2);

    if (!moved) {
      // Treat a click with no drag as a no-op: drop the degenerate draft.
      history.update((prev) => prev.slice(0, -1));
      return;
    }

    // Normalize a rectangle so width/height are always positive.
    if (tool === 'rect') {
      history.update((prev) => {
        const last = prev[prev.length - 1];
        if (!last || last.type !== 'rect') return prev;
        const normalized = {
          ...last,
          x: Math.min(last.x, last.x + last.width),
          y: Math.min(last.y, last.y + last.height),
          width: Math.abs(last.width),
          height: Math.abs(last.height),
        };
        return [...prev.slice(0, -1), normalized];
      });
    }

    history.commit(gestureBeforeRef.current);
    gestureBeforeRef.current = null;
  };

  // Export the live Konva stage at native image resolution. Both Save and Copy
  // build on this so they stay pixel-identical to what's on screen.
  const exportDataUrl = useCallback(() => {
    if (!image || !stageRef.current) return null;
    return stageRef.current.toDataURL({ mimeType: 'image/png', pixelRatio: 1 / scale });
  }, [image, scale]);

  const saveCanvasAsImage = useCallback(() => {
    const uri = exportDataUrl();
    if (!uri) return;
    const link = document.createElement('a');
    link.download = `${imageName}-annotated.png`;
    link.href = uri;
    link.click();
  }, [exportDataUrl, imageName]);

  const copyToClipboard = useCallback(async () => {
    const uri = exportDataUrl();
    if (!uri) return;
    try {
      const blob = await (await fetch(uri)).blob();
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      showToast('Copied to clipboard');
    } catch (error) {
      showToast('Copy failed — try saving instead', 'error');
    }
  }, [exportDataUrl, showToast]);

  const clearAll = useCallback(() => history.set([]), [history]);
  const resetNumbers = useCallback(
    () => history.set((prev) => prev.filter((m) => m.type !== 'number')),
    [history]
  );

  // Keyboard shortcuts: undo / redo.
  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tag = target && target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) history.redo();
        else history.undo();
      } else if (key === 'y') {
        e.preventDefault();
        history.redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [history]);

  const sidebarProps = {
    tool,
    onToolChange: setTool,
    prefs,
    setPref,
    onUpload: handleImageUpload,
    onSave: saveCanvasAsImage,
    onCopy: copyToClipboard,
    onClear: clearAll,
    onUndo: history.undo,
    onRedo: history.redo,
    onResetNumbers: resetNumbers,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    hasImage: !!image,
    hasMarks: marks.length > 0,
  };

  const renderMark = (mark, index) => {
    const strokeWidth = (mark.strokeWidth || 8) / scale;

    if (mark.type === 'circle') {
      return <Circle key={index} x={mark.x} y={mark.y} radius={mark.radius} fill={mark.fill} />;
    }

    if (mark.type === 'rect') {
      return (
        <Rect
          key={index}
          x={mark.x}
          y={mark.y}
          width={mark.width}
          height={mark.height}
          stroke={mark.color}
          strokeWidth={strokeWidth}
        />
      );
    }

    if (mark.type === 'arrow') {
      return (
        <Arrow
          key={index}
          points={mark.points}
          stroke={mark.color}
          fill={mark.color}
          strokeWidth={strokeWidth}
          pointerLength={(mark.strokeWidth * 2 + 6) / scale}
          pointerWidth={(mark.strokeWidth * 2 + 6) / scale}
          lineCap="round"
          lineJoin="round"
        />
      );
    }

    if (mark.type === 'x') {
      const [x, y, size] = mark.points;
      const s = size / scale;
      return (
        <React.Fragment key={index}>
          <Line points={[x - s, y - s, x + s, y + s]} stroke={mark.color} strokeWidth={strokeWidth} lineCap="round" lineJoin="round" />
          <Line points={[x - s, y + s, x + s, y - s]} stroke={mark.color} strokeWidth={strokeWidth} lineCap="round" lineJoin="round" />
        </React.Fragment>
      );
    }

    if (mark.type === 'text') {
      return (
        <KonvaText
          key={index}
          x={mark.x}
          y={mark.y}
          text={mark.text}
          fill={mark.color}
          fontSize={mark.fontSize / scale}
          fontStyle="bold"
        />
      );
    }

    if (mark.type === 'number') {
      const radius = mark.radius / scale;
      const label = String(mark.value);
      return (
        <Group key={index}>
          <Circle x={mark.x} y={mark.y} radius={radius} fill={mark.color} />
          <KonvaText
            x={mark.x - radius}
            y={mark.y - radius}
            width={radius * 2}
            height={radius * 2}
            text={label}
            fill="#ffffff"
            fontSize={radius * 1.1}
            fontStyle="bold"
            align="center"
            verticalAlign="middle"
          />
        </Group>
      );
    }

    // Freehand line (default).
    return (
      <Line
        key={index}
        points={mark.points}
        stroke={mark.color}
        strokeWidth={strokeWidth}
        tension={0.5}
        lineCap="round"
        lineJoin="round"
      />
    );
  };

  return (
    <div className="app">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar__brand">
          <div className="navbar__logo">
            <span className="navbar__logo-rs">RS</span>
          </div>
          <div className="navbar__title">
            <span className="navbar__title-main">Board Updater</span>
            <span className="navbar__title-sub">RuneScape Annotation Tool</span>
          </div>
        </div>
        <div className="navbar__actions">
          {image && (
            <span className="navbar__status">
              <span className="navbar__status-dot" />
              Image loaded
            </span>
          )}
          <label htmlFor="navbar-upload-file" className="navbar__upload-btn">
            <ImageIcon className="navbar__upload-icon" />
            <span className="navbar__upload-label">Upload</span>
          </label>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="navbar-upload-file"
            type="file"
            onChange={handleImageUpload}
          />
        </div>
      </nav>

      <div className="app-body">
        {/* Sidebar */}
        {isMobile ? (
          <>
            {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
            <aside className={`sidebar sidebar--mobile ${sidebarOpen ? 'sidebar--open' : ''}`}>
              <div className="sidebar__header">
                <span className="sidebar__header-title">Tools</span>
                <button className="sidebar__close-btn" onClick={() => setSidebarOpen(false)}>
                  <Close />
                </button>
              </div>
              <SidebarContent {...sidebarProps} />
            </aside>
          </>
        ) : (
          <aside className={`sidebar sidebar--desktop ${sidebarOpen ? 'sidebar--open' : ''}`}>
            <SidebarContent {...sidebarProps} />
          </aside>
        )}

        {/* Desktop toggle */}
        {!isMobile && (
          <button
            className={`sidebar-toggle ${sidebarOpen ? 'sidebar-toggle--open' : 'sidebar-toggle--closed'}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
          </button>
        )}

        {/* Mobile FAB */}
        {isMobile && !sidebarOpen && (
          <button className="mobile-fab" onClick={() => setSidebarOpen(true)} aria-label="Open tools">
            <Menu />
          </button>
        )}

        {/* Canvas Area (also the drop zone) */}
        <main {...getRootProps({ className: 'canvas-area' })}>
          <input {...getInputProps()} />
          {isDragActive && (
            <div className="drop-overlay">
              <ImageIcon className="drop-overlay__icon" />
              <span>Drop image to load</span>
            </div>
          )}
          <div className="canvas-wrapper">
            {!image && (
              <div className="empty-state">
                <div className="empty-state__icon">
                  <ImageIcon style={{ fontSize: '3rem', color: '#dc2626' }} />
                </div>
                <h2 className="empty-state__title">No Image Loaded</h2>
                <p className="empty-state__subtitle">
                  Upload, paste (Ctrl+V), or drag a board image to start annotating
                </p>
                <label htmlFor="empty-state-upload" className="empty-state__cta">
                  <ImageIcon className="empty-state__cta-icon" />
                  Upload Image
                </label>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="empty-state-upload"
                  type="file"
                  onChange={handleImageUpload}
                />
                <div className="empty-state__steps">
                  {[
                    { n: 1, text: 'Upload, paste, or drag a board screenshot' },
                    { n: 2, text: 'Pick a drawing tool from the sidebar' },
                    { n: 3, text: 'Draw circles, arrows, boxes, X marks, text or numbers' },
                    { n: 4, text: 'Undo with Ctrl+Z, then save or copy' },
                  ].map(({ n, text }) => (
                    <div key={n} className="empty-state__step">
                      <span className="empty-state__step-num">{n}</span>
                      <span className="empty-state__step-text">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Stage
              width={stageSize.width}
              height={stageSize.height}
              scaleX={scale}
              scaleY={scale}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
              ref={stageRef}
            >
              <Layer>
                {image && <KonvaImage image={image} />}
                {marks.map(renderMark)}
              </Layer>
            </Stage>
          </div>
        </main>
      </div>

      {toast && <div className={`toast toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
};

export default App;
